// src/utils/menuUtils.ts

export interface AppRoute {
  path: string;
  title: string;
  label?: string;
  icon?: any;
  element: React.ReactNode;
  layout?: 'main' | 'none';
  isMenu?: boolean;
  menuOrder?: number;
  noSidebar?: boolean;
}

/**
 * Determines which menu items should be shown based on the current URL path
 * @param appRoutes - All available routes
 * @param currentPath - Current URL pathname
 * @returns Filtered routes for the menu
 */
export const getFilteredMenuRoutes = (appRoutes: AppRoute[], currentPath: string): AppRoute[] => {
  // Determine the context based on the current path
  const isFinanceContext = currentPath.includes('/finance');
  const isAccountsContext = currentPath.includes('/accounts');
  const isMasterDataContext = currentPath.includes('/master-data');
  const isLeadsContext = currentPath.includes('/leads');

  return appRoutes.filter((route) => {
    // Always show routes that are not context-specific (like PL Applications)
    if (!route.isMenu) return false;
    
    // Always show AI Assistant regardless of context
    if (route.path === '/ai-chat') return true;
    
    // If we're in finance context, show finance-related routes
    if (isFinanceContext) {
      return route.path.includes('/finance') || 
             route.path === '/finance';
    }
    
    // If we're in accounts context, show accounts-related routes
    if (isAccountsContext) {
      return route.path.includes('/accounts') || 
             route.path === '/accounts';
    }

    // If we're in master-data context, show master-data routes
    if (isMasterDataContext) {
      return route.path.includes('/master-data') ||
             route.path === '/master-data';
    }

    // If we're in leads context, show leads routes
    if (isLeadsContext) {
      return route.path.includes('/leads') || route.path === '/leads';
    }
    
    // Default context (root or other paths) - show accounts routes and AI Assistant
    return route.path.includes('/accounts') || 
           route.path === '/accounts' ||
           route.path === '/ai-chat';
  });
};

/**
 * Gets the appropriate dashboard route based on the current context
 * @param currentPath - Current URL pathname
 * @returns Dashboard route path
 */
export const getDashboardRoute = (currentPath: string): string => {
  if (currentPath.includes('/finance')) {
    return '/finance';
  }
  if (currentPath.includes('/master-data')) {
    return '/master-data';
  }
  if (currentPath.includes('/leads')) {
    return '/leads';
  }
  return '/accounts';
};

/**
 * Determines the current context based on the URL path
 * @param currentPath - Current URL pathname
 * @returns Context type
 */
export const getCurrentContext = (
  currentPath: string
): 'accounts' | 'finance' | 'master-data' | 'leads' | 'default' => {
  if (currentPath.includes('/finance')) {
    return 'finance';
  }
  if (currentPath.includes('/master-data')) {
    return 'master-data';
  }
  if (currentPath.includes('/leads')) {
    return 'leads';
  }
  if (currentPath.includes('/accounts')) {
    return 'accounts';
  }
  return 'default';
};
